import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { cn } from '~in-app/lib/utils'

import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Input, Popover, PopoverContent, PopoverTrigger, ScrollArea } from './shadcn-ui'

type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> & Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
  onChange?: (value: { fullNumber: string, localNumber: string }) => void
}

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn('flex', className)}
      countrySelectComponent={CountrySelect}
      flagComponent={FlagComponent}
      inputComponent={InputComponent}
      smartCaret={false}
      onChange={
        (value) => {
          if (value) {
            try {
            // Check if the number is valid and parse it
              const phoneNumber = RPNInput.parsePhoneNumber(value)
              const fullNumber = phoneNumber?.number || value // Full number with country code
              const localNumber = phoneNumber?.nationalNumber || value.replace(/^\+/, '') // Local number without country code
              // Pass both full number and local number
              onChange?.({
                fullNumber,
                localNumber,
              })
            }
            catch (error) {
            // Handle incomplete or invalid numbers manually
              // Extract country code (first part of the value) and local number
              const countryCodeMatch = value.match(/^\+(\d{1,4})/) // Match the country code
              const countryCode = countryCodeMatch ? countryCodeMatch[0] : ''
              const localNumber = value.replace(countryCode, '') // Remove the country code from the input

              onChange?.({
                fullNumber: value,
                localNumber: localNumber || '', // Default to empty if nothing is left
              })
            }
          }
          else {
          // Handle empty value
            onChange?.({
              fullNumber: '',
              localNumber: '',
            })
          }
        }
      }
      {...props}
    />

  )
})

PhoneInput.displayName = 'PhoneInput'

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn('rounded-e-lg rounded-s-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0', className)}
      {...props}
      ref={ref}
    />
  ),
)

InputComponent.displayName = 'InputComponent'

type CountryEntry = { label: string, value: RPNInput.Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="flex gap-1 rounded-e-none rounded-s-lg border border-r-0 border-input bg-transparent px-3 text-muted-foreground focus:z-10"
          color="muted"
          disabled={disabled}
          type="button"
          variant="outlined"
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />

          <ChevronsUpDown
            className={
              cn(
                '-mr-2 size-4 opacity-50',
                disabled ? 'hidden' : 'opacity-100',
              )
            }
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search country..." />

          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>

              <CommandGroup>
                {
                  countryList.map(({ value, label }) =>
                    value
                      ? (
                          <CountrySelectOption
                            key={value}
                            country={value}
                            countryName={label}
                            selectedCountry={selectedCountry}
                            onChange={onChange}
                          />
                        )
                      : null,
                  )
                }
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country
  onChange: (country: RPNInput.Country) => void
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem
      className="gap-2"
      onSelect={() => onChange(country)}
    >
      <FlagComponent
        country={country}
        countryName={countryName}
      />

      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>

      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm border border-foreground/20 bg-foreground/20">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
